import type { DirectiveBinding, ObjectDirective } from 'vue';

interface IntersectionBindingValue {
  callback: (isIntersecting: boolean, entry: IntersectionObserverEntry) => void;
  intersection?: IntersectionObserverInit;
}

const vObserveVisibility: ObjectDirective<
  HTMLElement,
  IntersectionBindingValue | ((isIntersecting: boolean, entry: IntersectionObserverEntry) => void)
> = {
  mounted(
    element: HTMLElement,
    binding: DirectiveBinding<
      IntersectionBindingValue | ((isIntersecting: boolean, entry: IntersectionObserverEntry) => void)
    >,
  ) {
    let callback: ((isIntersecting: boolean, entry: IntersectionObserverEntry) => void) | undefined;
    let options: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px',
      threshold: 0.01,
    };

    if (typeof binding.value === 'function') {
      callback = binding.value;
    } else if (binding.value && typeof binding.value === 'object') {
      callback = binding.value.callback;

      if (binding.value.intersection) {
        options = { ...options, ...binding.value.intersection };
      }
    }

    if (!callback) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const isIntersecting = entry.isIntersecting;
        const target = entry.target as HTMLElement & {
          _wasIntersecting?: boolean;
        };

        const wasIntersecting = target._wasIntersecting ?? false;

        // Only notify on false → true transitions
        if (isIntersecting && !wasIntersecting) {
          callback!(true, entry);
        }

        target._wasIntersecting = isIntersecting;
      });
    }, options);

    observer.observe(element);
    (element as any)._observer = observer;
  },
  unmounted(element: HTMLElement) {
    const target = element as HTMLElement & {
      _observer?: IntersectionObserver;
      _wasIntersecting?: boolean;
    };

    target._observer?.disconnect();
    delete target._observer;
    delete target._wasIntersecting;
  },
};

export default vObserveVisibility;
